import { Semaphore } from 'async-mutex';
import { updateToken } from './functions';
import { DateTime } from 'luxon';

/* global fetch, window, Excel, localStorage */

const semaphore = new Semaphore(50);

export function getWorksheetFromRange(range: string) {
  let worksheet = range.split('!')[0];

  if (worksheet.startsWith("'") && worksheet.endsWith("'")) {
    worksheet = worksheet.substring(1, worksheet.length - 1);
  }

  return worksheet;
}

export async function doRequest(url: string | URL) {
  let lsSSO = localStorage.getItem('feeder_sso');
  let response;
  if (lsSSO !== null && JSON.parse(lsSSO)) {
    await semaphore.runExclusive(async () => await updateToken());
    response = await semaphore.runExclusive(
      async () =>
        await fetch(url, { headers: { 'OAuth-Token': window.OAuthToken } })
    );
    if (response.status === 401) {
      await semaphore.runExclusive(async () => await updateToken());
      response = await semaphore.runExclusive(
        async () =>
          await fetch(url, { headers: { 'OAuth-Token': window.OAuthToken } })
      );
    }
  } else {
    const apiKey = localStorage.getItem('feeder_api_key');
    response = await semaphore.runExclusive(
      async () => await fetch(url, { headers: { 'x-api-key': apiKey! } })
    );
  }
  return response;
}

// central b - addins

export async function getDateValue(date: string) {
  const context = new Excel.RequestContext();
  let result = context.workbook.functions.datevalue(date.split('T')[0]);
  result.load('value');
  await context.sync();
  return result.value;
}

export function setDateValues(prices: any): number[][] {
  prices.forEach((price: any[]) => {
    price[0] = dateTimeDateToExcelDate(
      DateTime.fromISO(price[0].split('+')[0])
    );
  });
  return prices;
}

export function excelDateToDateTimeDate(excelTimestamp: number) {
  return DateTime.fromObject({ year: 1900, month: 1, day: 1 }).plus({
    milliseconds: (excelTimestamp - 2) * 24 * 60 * 60 * 1000,
  });
}
export function dateTimeDateToExcelDate(date: DateTime) {
  let start = DateTime.fromObject({ year: 1900, month: 1, day: 1 });
  const diff =
    date.diff(start, 'milliseconds').milliseconds / (24 * 60 * 60 * 1000);
  if (start.offset !== date.offset) {
    const offsetDiff = (date.offset - start.offset) / (60 * 24);
    return diff + offsetDiff + 2;
  }
  return diff + 2;
}

export async function setNumberFormat(
  format: string,
  row_width: number,
  column_width: number,
  row_offset: number,
  column_offset: number,
  cell_start?: string,
  conditionOffset?: number
) {
  const context = new Excel.RequestContext();
  let range;
  if (cell_start === null || cell_start === undefined) {
    range = context.workbook
      .getSelectedRange()
      .getCell(0, 0)
      .getResizedRange(row_offset, column_offset)
      .getLastCell()
      .getResizedRange(row_width, column_width);
  } else if (cell_start.includes('!')) {
    range = context.workbook.worksheets
      .getItem(getWorksheetFromRange(cell_start))
      .getRange(cell_start.split('!')[1])
      .getResizedRange(row_offset, column_offset)
      .getLastCell()
      .getResizedRange(row_width, column_width);
  } else {
    range = context.workbook.worksheets
      .getActiveWorksheet()
      .getRange(cell_start)
      .getResizedRange(row_offset, column_offset)
      .getLastCell()
      .getResizedRange(row_width, column_width);
  }
  if (conditionOffset === undefined) {
    range.getColumn(0).numberFormat = [[format]];
    for (let i = 0; i < column_width!; i++) {
      range.getColumn(i + 1).numberFormat = [['0.00']];
    }
  } else {
    const conditionalFormat = range.conditionalFormats.add(
      Excel.ConditionalFormatType.custom
    );
    conditionalFormat.custom.rule.formulaLocal =
      '=(BEREICH.VERSCHIEBEN(INDIREKT(ADRESSE(ZEILE();SPALTE()));0;' +
      conditionOffset +
      ')="")*(INDIREKT(ADRESSE(ZEILE();SPALTE()))<>"-")';
    conditionalFormat.custom.format.numberFormat = format;
  }

  await context.sync();
}

export function transformResolutionAggregation(resolution?: string) {
  switch (resolution) {
    case '1min':
      return '1 min';
    case '5min':
      return '5 min';
    case 'quarter-hourly':
      return '15 min';
    case 'half-hourly':
      return '30 min';
    case 'hourly':
      return '1 hour';
    case 'daily':
      return '1 day';
    case 'weekly':
      return '1 week';
    case 'monthly':
      return '1 month';
    case 'quarterly':
      return '1 quarter';
    case 'seasonly':
      return '6 month';
    case 'yearly':
      return '1 year';
    default:
      return resolution;
  }
}

export function getNumberFormatFromResolution(resolution: string) {
  switch (resolution) {
    case '1 min':
    case '5 min':
    case '15 min':
    case '1 hour':
      return 'dd.mm.yyyy hh:MM';
    case '1 day':
    case '1 days':
    case '1 week':
    case '1 quarter':
    case '6 month':
    case '1 year':
      return 'dd.mm.yyyy';
    case '1 month':
      return 'mm.yyyy';
  }
  return 'dd.mm.yyyy';
}

export function getVersionFromString(max_version: string | number) {
  if (typeof max_version === 'string') {
    if (max_version.match(/\bv('[A-Za-z0-9]+|\d+)\b/g)) {
      return max_version.substring(1);
    } else if (
      max_version.match(/\b\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2})?)?\b/g)
    ) {
      return max_version;
    } else if (max_version.match(/\b(year|month):(\d+)\b/g)) {
      const vals = max_version.split(':');
      return vals[0].replace(/ /g, '') + ' ' + vals[1].replace(/ /g, '');
    }
  } else {
    return excelDateToDateTimeDate(max_version).toFormat(
      "yyyy-MM-dd'T'HH:mm:ss"
    );
  }
  return 0;
}

export function getLocalizedBoolean(value?: boolean) {
  const lang = Office.context.displayLanguage.split('-')[0];
  switch (lang.toLowerCase()) {
    case 'en':
      return value ? 'TRUE' : 'FALSE';
    case 'de':
      return value ? 'WAHR' : 'FALSCH';
    default:
      return value ? 'TRUE' : 'FALSE';
  }
}
